import clsx from "clsx";
import { type ReactElement, useId } from "react";
import { I } from "@/components/design";
import { useNavigationPending } from "@/hooks/useNavigationPending";

interface NavItemProps {
  icon: ReactElement;
  label: string;
  /** Route this entry navigates to — drives the pending state. */
  to: string;
  isActive: boolean;
  onClick: () => void;
  /** Short count shown at the row's end, e.g. "3" or "5+". */
  badge?: string;
  /** Spoken in place of the bare badge text, as the entry's description. */
  badgeLabel?: string;
}

/**
 * A sidebar entry. Every destination is a lazily-loaded route, so React Router
 * blocks the navigation while the chunk downloads and holds the CURRENT page on
 * screen for all of it — measured at 634-1138ms against the production build
 * with a realistic round trip, and the Products route alone pulls five chunks.
 *
 * Nothing on screen moves during that window, so without a pending state the
 * entry reads as a dead button and invites a second click. The spinner replaces
 * the icon rather than joining it, so the row does not change width mid-click.
 */
export function NavItem({ icon, label, to, isActive, onClick, badge, badgeLabel }: NavItemProps) {
  const pending = useNavigationPending(to);
  const badgeLabelId = useId();
  // The count is the entry's description, not part of its name: inside this
  // flex row any separator between the two collapses, and screen readers ran
  // them together ("InboxMore than 5…").
  const describedBy = badge && badgeLabel ? badgeLabelId : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-describedby={describedBy}
      disabled={pending}
      aria-busy={pending}
      className={clsx("nav-item", isActive && "is-active")}
      aria-current={isActive ? "page" : undefined}
    >
      <span className="nav-icon">{pending ? <I.spinner size={15} className="animate-spin" /> : icon}</span>
      <span className="nav-label">{label}</span>
      {badge ? (
        <>
          <span className="nav-badge" aria-hidden>
            {badge}
          </span>
          {/* Hidden from the name; aria-describedby still reads it. */}
          {badgeLabel ? (
            <span id={badgeLabelId} hidden>
              {badgeLabel}
            </span>
          ) : null}
        </>
      ) : null}
    </button>
  );
}
